// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Lustre — the menu, the panels and the results table.
//
// THE DISMISSAL CONTRACT is implemented once, here, and every overlay in the game goes through it:
// a >=44px close control, a tap anywhere outside the panel, Escape, and a backdrop that is genuinely
// reachable. An overlay with one way out is a game you cannot play, and on a phone the button that
// opened it is underneath the thing covering it.

import { MODES, MODE_IDS, type ModeId } from './modes';
import { HUE_GLYPH, TIERS } from './cards';
import type { Standing, State } from './rules';

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export const FOOTER_HTML = `<footer class="site-footer">
  Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
  · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
</footer>`;

export const HOW_HTML = `
  <h2>How to play</h2>
  <p>A rough spar on your bench is either your <em>engine</em> or your <em>score</em> — never both.
    Turning one into the other is the whole game.</p>
  <ul class="how">
    <li><b>Spend ticks, not turns.</b> Everyone shares one wheel. Whoever is furthest back acts, so a
      cheap move gets you another go sooner. When your pawn reaches the end you are done.</li>
    <li><b>Gather grit, claim spars.</b> A spar you own discounts every future spar of the
      <em>other</em> hues, and drives your wheel so you gather more each time.</li>
    <li><b>Cut a spar to score it.</b> It pays its number in lustre — and then it is finished: no more
      discount, no more bite. Your bench only holds six, so something has to go.</li>
    <li><b>The price of cutting is shared.</b> Every point <em>anybody</em> banks pushes the dial, and
      the dial sets what a cut costs in ticks. Convert early and it is cheap; hoard, and the table
      raises the price on you while you wait.</li>
    <li><b>Patrons pay for pips.</b> But cutting destroys pips — so take the order first.</li>
  </ul>
  <p class="fine">Tap something to arm it, then tap the big bar to commit. The bar always says exactly
    what will happen. On a keyboard: 1–4 take grit, Enter commits, Escape clears.</p>`;

export const aboutHtml = (): string => `
  <h2>About Lustre</h2>
  <p>An engine-builder where the engine and the score are the same object, and the exchange rate is a
    public dial that everybody at the table pushes.</p>
  <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>.
    No accounts, no cookies, no trackers — just anonymous, cookie-less page counts.</p>
  <p class="fine">Playing with friends connects your browsers <b>directly to each other</b> over
    WebRTC. A public relay is used only to introduce them; no game state ever touches a server, and
    there is no server to touch. Connecting does reveal your IP address to the people in your room,
    which is how peer-to-peer works everywhere.</p>
  <p class="fine">Nothing in Lustre is hidden — both decks are dealt from one shared seed and every
    card is face up. That is why a room needs no host at all: each browser replays the same list of
    moves and arrives at the same table, so somebody closing their laptop cannot pause the game.</p>`;

export function menuHtml(mode: ModeId, muted: boolean, best: number): string {
  return `<div class="main-content menu">
    <h1 class="wordmark">Lustre</h1>
    <p class="tagline">Your engine is your score. Spend it, or watch the price of spending it rise.</p>
    <div class="modepick" role="group" aria-label="Mode">
      ${MODE_IDS.map(
        (id) =>
          `<button type="button" class="mode${id === mode ? ' on' : ''}" data-mode="${id}">
             <b>${MODES[id].name}</b><span>${esc(MODES[id].blurb)}</span></button>`,
      ).join('')}
    </div>
    <div class="menubtns">
      <button id="playSolo" class="primary" type="button">Play</button>
      <button id="playFriends" class="secondary" type="button">Play with friends</button>
    </div>
    <p class="bestline">${best > 0 ? `Your best in ${MODES[mode].name}: <b>${best}</b>` : 'No round yet in this mode.'}</p>
    <div class="menufoot">
      <button id="howBtn" class="link" type="button">How to play</button>
      <button id="aboutBtn" class="link" type="button">About</button>
      <button id="muteBtn" class="link" type="button" aria-pressed="${muted}">${muted ? 'Sound off' : 'Sound on'}</button>
    </div>
  </div>${FOOTER_HTML}`;
}

/**
 * Open a panel that can always be closed.
 *
 * Four exits, every time. The backdrop listener is armed on a delay because a panel opened from a
 * pointerup handler is otherwise closed instantly by that same tap's trailing click.
 */
export function openPanel(host: HTMLElement, html: string): void {
  const back = document.createElement('div');
  back.className = 'backdrop';
  back.innerHTML = `<div class="sheet" role="dialog" aria-modal="true">
    <button class="sheetx" type="button" aria-label="Close">✕</button>${html}</div>`;
  host.append(back);
  const panel = back.querySelector('.sheet')!;
  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    back.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  back.querySelector('.sheetx')!.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  setTimeout(() => {
    back.addEventListener('pointerdown', (e) => {
      if (!panel.contains(e.target as Node)) close();
    });
  }, 350);
}

/** One rival's whole board, on demand — so "what are they actually building" is always answerable. */
export function rivalHtml(s: State, seat: number, name: string): string {
  const p = s.players[seat];
  const spars = p.bench
    .map((sp) => {
      const state = sp.cut ? 'cut' : sp.spent ? 'spent' : 'rough';
      return `<i class="tag ${state} h${sp.hue}">${HUE_GLYPH[sp.hue]} ${TIERS[sp.tier].name}${
        sp.cut ? ' ✦' : sp.spent ? ' ·' : ''
      }</i>`;
    })
    .join('');
  const orders = p.patrons.map((id) => `<i class="tag">order +${s.patronDefs[id].value}</i>`).join('');
  return `<h2>${esc(name)}</h2>
    <p class="fine">Tick ${p.pawn} of ${s.close} · ${p.lustre} banked · ${p.shelf.length} shelved</p>
    <div class="tags">${spars || '<i class="tag">nothing claimed yet</i>'}</div>
    <div class="tags">${orders}</div>`;
}

/**
 * The results table.
 *
 * EVERY seat gets a row with a term-by-term breakdown — not a name and a number — because this is
 * the one moment players compare themselves, and "you scored 11" tells you nothing about what the
 * person next to you did differently. The stranded row is the "what everyone missed" line: spars
 * bought and never cut are worth exactly nothing, and seeing four of them next to somebody else's
 * one is the whole lesson of the game in a single number.
 */
export function summaryHtml(rows: Standing[], names: string[], mySeat: number, mode: ModeId, waiting: string): string {
  const body = rows
    .map(
      (r, i) => `<li class="res${r.seat === mySeat ? ' you' : ''}">
        <div class="res-h"><span class="rank">${i + 1}</span>
          <b>${esc(r.seat === mySeat ? 'You' : (names[r.seat] ?? `Seat ${r.seat + 1}`))}</b>
          <u>${r.total}<i>✦</i></u></div>
        <div class="res-t">
          ${MODES[mode].patronIsVerb ? '' : `<span>cut <b>${r.cut}</b></span>`}
          ${r.patron > 0 ? `<span>orders <b>${r.patron}</b></span>` : ''}
          ${MODES[mode].patronIsVerb ? '' : `<span>conversions <b>${r.cuts}</b></span>`}
          <span class="${r.stranded > 0 ? 'bad' : ''}">stranded <b>${r.stranded}</b></span>
          <span>bite <b>${r.bite}</b></span>
          <span>grit left <b>${r.grit}</b></span>
        </div></li>`,
    )
    .join('');

  const stranded = rows.reduce((a, r) => a + r.stranded, 0);
  const lesson =
    stranded === 0
      ? 'Nobody left a spar uncut — the whole table converted everything it built.'
      : `<b>${stranded}</b> spar${stranded === 1 ? ' was' : 's were'} left on benches at the close, worth nothing.`;

  return `<div class="main-content results">
    <h2>The wheel stops</h2>
    ${waiting ? `<p class="fine" id="resWaitNote">${esc(waiting)}</p>` : ''}
    <ol class="reslist">${body}</ol>
    <p class="missed">${lesson}</p>
    <div class="menubtns">
      <button id="againBtn" class="primary" type="button">Play again</button>
      <button id="lobbyBtn" class="secondary" type="button" hidden>Back to lobby</button>
      <button id="menuBtn" class="secondary" type="button">Menu</button>
    </div>
    <p class="fine" id="resWait" hidden></p>
    <p class="fine"><button class="link results-feedback" type="button">Something feel wrong? Tell me</button></p>
  </div>${FOOTER_HTML}`;
}
